/**
 * The table's pagination: the pure page arithmetic, and the footer that drives it.
 *
 * Split the way the rest of this component is — `resolvePagination` is a pure
 * function of (policy, rowCount, page) with no refs and no DOM, so the paging
 * contract can be asserted in a unit test without rendering a table, and
 * `PaginationFooter` is presentation over the numbers it returns.
 *
 * ── Why pagination lives in `DataTable` at all ────────────────────────────────
 *
 * Because the alternative is every embedded table re-deriving it. A table placed
 * in a page cannot bound its own height without becoming a scroll trap: the wheel
 * stops the page and starts the table as soon as the pointer crosses it, and the
 * user has no way to predict which one is about to move. Paging is the way out,
 * and it is not something a caller should have to hand-roll — the slice is easy to
 * get right and the clamping, the ARIA and the mobile layout are not.
 */
import { twMerge } from "tailwind-merge";
import { Button } from "@/components/Button";
import type { DataTablePagination, TableDensity } from "./DataTable.types";

/**
 * Rows per page when `pagination` is `true` or omits `pageSize`.
 *
 * 10 rather than 25: the default exists for tables embedded in a page, and a page
 * of 10 is about a screen of rows on a laptop without pushing the footer — and the
 * control that follows it — below the fold. A caller showing a denser table says so.
 */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Upper bound on a page, mirroring the row budget a bounded table already honours.
 *
 * A page is a viewport's worth of rows, and it renders unvirtualized — so an
 * unbounded `pageSize` reintroduces exactly the mount cost paging is meant to
 * avoid. Clamped rather than defaulted, because "show 2,000" is a legible intent
 * expressed in a field that cannot carry it; the nearest honest answer is the
 * largest page there is.
 */
export const MAX_PAGE_SIZE = 200;

/** A usable page size, or the default when the value is not one. */
function resolvePageSize(value: number | undefined): number {
  if (value === undefined) return DEFAULT_PAGE_SIZE;
  // `0`, `2.5` and negatives have no reading as a page size and fall back. The
  // guard matters because `pageSize` reaches this from props with no schema in
  // front of it — a `0` would otherwise divide the row count to `Infinity` pages.
  if (!Number.isInteger(value) || value <= 0) return DEFAULT_PAGE_SIZE;
  return Math.min(value, MAX_PAGE_SIZE);
}

/** What a render needs once the paging policy has been resolved against the data. */
export interface ResolvedPagination {
  /** 0-based page being shown, clamped into range. */
  page: number;
  /** Total pages, always at least 1 so an empty table still reads "1 / 1". */
  pageCount: number;
  /** Index of the first row on this page, for slicing. */
  start: number;
  /** Index past the last row on this page, for slicing. */
  end: number;
  /** 1-based inclusive ordinal of the first row shown, or 0 when there are none. */
  from: number;
  /** 1-based inclusive ordinal of the last row shown. */
  to: number;
  /** Rows across every page. */
  total: number;
}

/**
 * Resolve a paging policy against the row count actually in play.
 *
 * `page` is the value to clamp — whichever of the controlled prop or the table's
 * own state is in force. Clamping here rather than at the call site is what keeps
 * a shrinking dataset from stranding a live page: a refetch returning fewer rows
 * under page 4 of 2 renders page 2, not an empty table under a lying footer.
 */
export function resolvePagination(
  pagination: DataTablePagination | boolean | undefined,
  rowCount: number,
  page: number
): ResolvedPagination {
  const config = typeof pagination === "boolean" ? {} : (pagination ?? {});
  const pageSize = resolvePageSize(config.pageSize);
  // `totalRows` means `data` is already one page from a server, so the count to
  // page over is the server's, not the array's.
  const total = config.totalRows ?? rowCount;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(Math.trunc(page) || 0, 0), pageCount - 1);
  const start = current * pageSize;
  // Against `rowCount`, not `total`: with `totalRows` set the array holds only
  // this page, so the slice must not run off its end.
  const end = Math.min(start + pageSize, config.totalRows !== undefined ? rowCount : total);
  const shown = Math.max(0, end - (config.totalRows !== undefined ? 0 : start));
  return {
    page: current,
    pageCount,
    start,
    end,
    from: total === 0 ? 0 : start + 1,
    to: total === 0 ? 0 : start + shown,
    total,
  };
}

const densityPadding: Record<TableDensity, string> = {
  compact: "px-3 py-1.5",
  default: "px-3 py-2",
  relaxed: "px-4 py-3",
};

export interface PaginationFooterProps extends ResolvedPagination {
  density: TableDensity;
  onPageChange: (next: number) => void;
  /** Labels the control for assistive tech, e.g. "Commands pagination". */
  label?: string;
}

/**
 * Page controls, and the range they are moving through.
 *
 * ── What the layout is doing ─────────────────────────────────────────────────
 *
 * The range ("9–16 of 312") is the load-bearing half and the arrows are the cheap
 * half: a paged table's real job is telling you how much there IS, which is the
 * one thing a scrollbar communicates badly.
 *
 * ── Mobile ───────────────────────────────────────────────────────────────────
 *
 * Three things change below `sm`, and each is a thumb problem rather than a width
 * problem:
 *
 *   - The buttons carry their labels ("Previous" / "Next") instead of bare carets.
 *     A 32px caret is under the 44px touch target every mobile guideline asks for,
 *     and it is the only control on the row — there is room to say what it does.
 *   - They sit at the EDGES of the row, thumb-reachable, with the range centred
 *     between them. On desktop the pair stays grouped at the right, where a mouse
 *     is already heading after a sort or a row click.
 *   - The range says "9–16 of 312" on both, but the page counter ("2 / 32") is
 *     desktop-only: on a phone it is a third number competing with the two that
 *     matter, and the arrows already say where you can go.
 *
 * Container queries rather than viewport breakpoints (`@sm:` off the table's own
 * wrapper), because the table is a block that gets placed in panes narrower than
 * the window — a 380px side panel on a 1440px monitor is mobile for this control
 * even though the viewport says otherwise.
 */
export function PaginationFooter({
  page,
  pageCount,
  from,
  to,
  total,
  density,
  onPageChange,
  label = "Pagination",
}: PaginationFooterProps) {
  const atStart = page <= 0;
  const atEnd = page >= pageCount - 1;

  return (
    <nav
      data-testid="data-table-pagination"
      aria-label={label}
      className={twMerge(
        "flex items-center gap-3 border-t border-border-default",
        /*
         * Mobile: `justify-between` pushes the two controls to opposite edges, where
         * thumbs are, with the range floating between them.
         *
         * Desktop: `justify-start`, and the range's own `@sm:mr-auto` is what opens
         * the gap — that keeps the two buttons adjacent as a group. Leaving
         * `justify-between` on at both sizes spread them ~500px apart in a wide pane,
         * which reads as two unrelated controls rather than one pager.
         */
        "justify-between @sm:justify-start",
        densityPadding[density]
      )}
    >
      <Button
        size="sm"
        variant="ghost"
        icon="CaretLeft"
        isDisabled={atStart}
        onPress={() => onPageChange(page - 1)}
        aria-label="Previous page"
        /* Ordered first on mobile so it sits at the leading edge; on desktop it
           rejoins the group after the range. */
        className="order-1 @sm:order-2"
      >
        {/* The label is present in the DOM on both and hidden by the container
            query, rather than swapped on a JS breakpoint: a width-conditional
            render would need a measurement pass and would flash the wrong control
            on first paint. */}
        <span className="@sm:hidden">Previous</span>
      </Button>

      <p
        className={twMerge(
          "m-0 flex min-w-0 items-baseline gap-2 text-body-sm text-text-secondary",
          /*
           * `@sm:grow` rather than `@sm:mr-auto`: `m-0` above and an auto margin are
           * the same Tailwind group, so `twMerge` resolves the conflict in favour of
           * whichever it sees last and the margin silently disappears. Growing the
           * range instead takes the slack with no class conflict, and puts the two
           * controls at the trailing edge where a mouse is already heading.
           */
          "order-2 @sm:order-1 @sm:grow"
        )}
      >
        {/*
          ONE statement, in rows.

          This used to print the row range and a page counter side by side —
          "1–6 of 6" then "1 / 1" — and at a 12px gap they ran together as
          "1–6 of 61 / 1". Two counting systems in one line, and the reader has to
          work out which number belongs to which.

          Rows win because rows are what someone is looking for; pages are an
          artifact of how we chose to chunk them. "Showing 11–20 of 312" says where
          you are and how much there is, and the arrows already say a page exists on
          either side — so the page ordinal is a third number that answers a question
          nobody asked. It stays in the live region below, where a screen reader user
          (who cannot see the arrows' disabled state) still needs it.

          "Showing" rather than a bare range: the bare form reads as a label whose
          subject went missing, and it is the run-on above that made that obvious.
        */}
        <span className="truncate tabular-nums">
          {total === 0 ? "No rows" : `Showing ${from}–${to} of ${total.toLocaleString()}`}
        </span>
      </p>

      <Button
        size="sm"
        variant="ghost"
        icon="CaretRight"
        iconPosition="right"
        isDisabled={atEnd}
        onPress={() => onPageChange(page + 1)}
        aria-label="Next page"
        className="order-3"
      >
        <span className="@sm:hidden">Next</span>
      </Button>

      {/*
        The page change announced once, politely.

        Without it a screen reader user presses "Next" and hears nothing: the rows
        swap silently, and the button they are still focused on has not changed. The
        range is the same sentence the sighted user reads, so both get told the same
        thing.
      */}
      <span aria-live="polite" aria-atomic="true" className="sr-only">
        {total === 0 ? "No rows" : `Page ${page + 1} of ${pageCount}, showing rows ${from} to ${to} of ${total}`}
      </span>
    </nav>
  );
}
